/* ============================================================================
   LOG.SYS — log-desktop.js
   Retro-Mac desktop blog subsystem for the BLOQUE theme.
   Loaded ONLY by sections/bq-log-desktop.liquid. Zero dependencies.
   Documented exception to the "global.js only" rule (docs/LOG-SPEC.md).

   Binds to DATA ATTRIBUTES only — never to bq-log-* class names.

   Desktop contract (bq-log-desktop.liquid):
     [data-log-desktop]                 root (+ data-log-store-url, unused here)
     [data-log-path]                    menubar path readout
     [data-log-clock]                   menubar clock (HH:MM AM, ticks 15s)
     [data-log-view]                    icon field, all icons always rendered
     [data-log-folder="tag-handle"]     folder icon <a href=".../tagged/handle">
     [data-log-file]                    file icon <a href="article-url">
        + data-log-handle, data-log-title, data-log-tags="handle handle"
     [data-log-up]                      UP.DIR <a hidden href="blog-url">
     [data-log-layer]                   window layer
     template[data-log-win-tpl]         one window frame (bq-log-win-frame)

   Window frame contract (bq-log-win-frame.liquid / server-rendered windows):
     [data-log-win]                     window root (+ data-log-handle)
     [data-log-titlebar]                drag handle / dblclick roll-up
     [data-log-win-title]               title plate text
     [data-log-close]                   close <button>
     [data-log-zoom]                    roll-up <button>
     [data-log-win-body]                content slot
     [data-log-preopened]               server-rendered article window
                                        (article.desktop; adopted, never re-fetched)
     data-log-url (optional)            source URL for a server-rendered window

   State attributes set by this file (styled by log-desktop.css):
     [data-log-rolled] on window root, [data-log-dragging] on window root.

   Persistence: localStorage "bloque-log-v1"
     { v:1, path:"", wins:[{h,u,t,x,y,z,rolled}], z:100 }
   Every storage touch is wrapped in try/catch — private mode degrades to
   session-only, everything still works.
   ========================================================================== */
(function () {
  'use strict';

  var STORE_KEY = 'bloque-log-v1';
  var SECTION_ID = 'bq-log-window';
  var mqMobile = window.matchMedia('(max-width: 759.98px)');
  var mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- helpers ---------- */

  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) { return null; }
  }

  function writeStore(obj) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(obj)); } catch (err) { /* private mode: session-only */ }
  }

  function cssEsc(s) {
    s = String(s);
    return (window.CSS && window.CSS.escape) ? window.CSS.escape(s) : s.replace(/["\\]/g, '\\$&');
  }

  function clamp(n, lo, hi) { return Math.min(Math.max(n, lo), hi); }

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function isNum(n) { return typeof n === 'number' && isFinite(n); }

  /* "the rain, again" → "THE RAIN AGAIN.TXT" */
  function fileName(s) {
    var n = String(s || '')
      .toUpperCase()
      .replace(/['\u2018\u2019]/g, '')
      .replace(/[^A-Z0-9]+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\s+TXT$/, '');
    if (!n) n = 'UNTITLED';
    return n + '.TXT';
  }

  function sectionUrl(u) {
    return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'sections=' + SECTION_ID;
  }

  /* ---------- per-desktop init ---------- */

  function initDesktop(root) {
    if (root.getAttribute('data-log-ready')) return;
    root.setAttribute('data-log-ready', '1');

    var view = root.querySelector('[data-log-view]');
    var layer = root.querySelector('[data-log-layer]');
    var tpl = root.querySelector('[data-log-win-tpl]');
    var pathEl = root.querySelector('[data-log-path]');
    var clockEl = root.querySelector('[data-log-clock]');
    var upEl = root.querySelector('[data-log-up]');
    if (!view || !layer) return;

    var wins = Object.create(null); /* handle → {h,u,t,x,y,z,rolled,el} */
    var zTop = 100;
    var path = '';
    /* server-filtered /tagged/ page: only the current tag's files are in the
       DOM, so folder/UP.DIR clicks must navigate for real (see click handler) */
    var filteredPage = /\/tagged\//.test(window.location.pathname);
    var cascadeI = 0;
    var saveTimer = null;

    /* ---------- persistence ---------- */

    function snapshot() {
      var arr = Object.keys(wins).map(function (k) { return wins[k]; })
        .sort(function (a, b) { return a.z - b.z; })
        .map(function (w) {
          return { h: w.h, u: w.u, t: w.t, x: w.x, y: w.y, z: w.z, rolled: !!w.rolled };
        });
      return { v: 1, path: path, wins: arr, z: zTop };
    }

    function save() {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(function () {
        saveTimer = null;
        writeStore(snapshot());
      }, 300);
    }

    function flushSave() {
      if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
        writeStore(snapshot());
      }
    }

    /* ---------- clock: HH:MM AM, ticks every 15s ---------- */

    function tickClock() {
      if (!clockEl) return;
      if (!root.isConnected) { clearInterval(clockTimer); return; }
      var d = new Date();
      var h = d.getHours();
      var ap = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      if (h === 0) h = 12;
      clockEl.textContent = pad2(h) + ':' + pad2(d.getMinutes()) + ' ' + ap;
    }
    var clockTimer = null;
    if (clockEl) {
      tickClock();
      clockTimer = setInterval(tickClock, 15000);
    }

    /* ---------- view state: folder drill-down ---------- */

    function hasFolder(h) {
      return !!view.querySelector('[data-log-folder="' + cssEsc(h) + '"]');
    }

    function applyView(next, silent) {
      path = next || '';
      var i, el, tags;
      var folders = view.querySelectorAll('[data-log-folder]');
      for (i = 0; i < folders.length; i++) folders[i].hidden = !!path;
      var files = view.querySelectorAll('[data-log-file]');
      for (i = 0; i < files.length; i++) {
        el = files[i];
        tags = (el.getAttribute('data-log-tags') || '').split(/\s+/).filter(Boolean);
        el.hidden = path ? tags.indexOf(path) === -1 : tags.length > 0;
      }
      if (upEl) upEl.hidden = !path;
      if (pathEl) pathEl.textContent = 'LOG: /' + (path ? path.toUpperCase() + '/' : '');
      if (!silent) save();
    }

    /* steps() zoom-rect flourish; skipped under reduced motion + on mobile */
    function flourish(from, to) {
      if (mqReduced.matches || mqMobile.matches || !from || !to) return;
      var el = document.createElement('div');
      el.className = 'bq-log-zoomrect';
      el.setAttribute('data-log-zoomrect', '');
      el.setAttribute('aria-hidden', 'true');
      el.style.left = from.left + 'px';
      el.style.top = from.top + 'px';
      el.style.width = from.width + 'px';
      el.style.height = from.height + 'px';
      root.appendChild(el);
      if (typeof el.animate !== 'function') { el.remove(); return; }
      try {
        var anim = el.animate([
          { left: from.left + 'px', top: from.top + 'px', width: from.width + 'px', height: from.height + 'px' },
          { left: to.left + 'px', top: to.top + 'px', width: to.width + 'px', height: to.height + 'px' }
        ], { duration: 240, easing: 'steps(6, end)' });
        anim.onfinish = anim.oncancel = function () { el.remove(); };
        setTimeout(function () { if (el.parentNode) el.remove(); }, 600); /* safety net */
      } catch (err) { el.remove(); }
    }

    /* ---------- window registry ---------- */

    function recOf(winEl) {
      var h = winEl.getAttribute('data-log-handle');
      return h ? wins[h] : null;
    }

    function slotTaken(x, y) {
      /* wins is a null-prototype object: for..in only yields own keys */
      for (var h in wins) {
        var r = wins[h];
        if (!r) continue;
        if (isNum(r.x) && isNum(r.y) && Math.abs(r.x - x) < 12 && Math.abs(r.y - y) < 12) return true;
      }
      return false;
    }

    /* next free cascade slot — restored windows keep their saved spots, so
       skip any slot already occupied instead of zero-stacking on them */
    function cascade() {
      var x = 60, y = 60, tries;
      for (tries = 0; tries < 8; tries++) {
        var i = cascadeI++ % 8;
        x = clamp(60 + 24 * i, 0, Math.max(0, layer.clientWidth - 260));
        y = clamp(60 + 24 * i, 0, Math.max(0, layer.clientHeight - 120));
        if (!slotTaken(x, y)) break;
      }
      return { x: x, y: y };
    }

    function applyGeom(rec) {
      rec.el.style.left = rec.x + 'px';
      rec.el.style.top = rec.y + 'px';
      rec.el.style.zIndex = rec.z;
      /* mobile stacked mode: frontmost first (flex order, ignored on desktop) */
      rec.el.style.order = String(-rec.z);
    }

    function dressWin(w, title) {
      w.setAttribute('role', 'dialog');
      w.setAttribute('aria-modal', 'false');
      w.setAttribute('aria-label', title);
      w.setAttribute('tabindex', '-1');
    }

    function focusWin(w) {
      try { w.focus({ preventScroll: true }); } catch (err) { try { w.focus(); } catch (e2) {} }
    }

    function bringFront(w) {
      var rec = recOf(w);
      if (!rec) return;
      if (rec.z === zTop) return; /* already frontmost */
      rec.z = ++zTop;
      applyGeom(rec);
      save();
    }

    function toggleRoll(w) {
      if (mqMobile.matches) return; /* roll-up disabled on mobile */
      var rec = recOf(w);
      if (!rec) return;
      rec.rolled = !rec.rolled;
      if (rec.rolled) w.setAttribute('data-log-rolled', '');
      else w.removeAttribute('data-log-rolled');
      save();
    }

    function closeWin(w, viaKeyboard) {
      var h = w.getAttribute('data-log-handle');
      var hadFocus = w.contains(document.activeElement);
      w.remove();
      if (h && wins[h]) delete wins[h];
      save();
      if (hadFocus || viaKeyboard) {
        /* Escape / keyboard close: return focus to the matching icon */
        var icon = h ? view.querySelector('[data-log-file][data-log-handle="' + cssEsc(h) + '"]') : null;
        if (icon && !icon.hidden) { icon.focus(); return; }
        var first = view.querySelector('[data-log-file]:not([hidden]), [data-log-folder]:not([hidden]), [data-log-up]:not([hidden])');
        if (first) first.focus();
      }
    }

    /* ---------- window creation + content ---------- */

    function makeWin(opts) {
      if (!tpl || !tpl.content) return null;
      var frag = tpl.content.cloneNode(true);
      var w = frag.querySelector('[data-log-win]') || frag.firstElementChild;
      if (!w) return null;
      var rec = { h: opts.h, u: opts.u || '', t: opts.t, rolled: !!opts.rolled, z: opts.z, el: w };
      if (isNum(opts.x) && isNum(opts.y)) { rec.x = opts.x; rec.y = opts.y; }
      else { var p = cascade(); rec.x = p.x; rec.y = p.y; }
      w.setAttribute('data-log-handle', rec.h);
      dressWin(w, rec.t);
      if (rec.rolled) w.setAttribute('data-log-rolled', '');
      var titleEl = w.querySelector('[data-log-win-title]');
      if (titleEl) titleEl.textContent = rec.t;
      applyGeom(rec);
      layer.appendChild(w);
      wins[rec.h] = rec;
      return rec;
    }

    function showError(body, rec) {
      body.textContent = '';
      var box = document.createElement('div');
      box.className = 'bq-log-error';
      box.setAttribute('data-log-error', '');
      var line = document.createElement('p');
      line.textContent = 'ERR 0x2F: LOG ENTRY COULD NOT BE READ.';
      box.appendChild(line);
      if (rec.u) {
        var a = document.createElement('a');
        a.href = rec.u;
        a.textContent = 'OPEN AS PAGE \u2192';
        box.appendChild(a);
      }
      body.appendChild(box);
    }

    /* Section Rendering API: GET url?sections=bq-log-window → { "bq-log-window": html } */
    function loadContent(rec) {
      var body = rec.el.querySelector('[data-log-win-body]');
      if (!body) return;
      if (!rec.u) { showError(body, rec); return; }
      fetch(sectionUrl(rec.u), { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (json) {
          var html = json && json[SECTION_ID];
          if (!html) throw new Error('empty section payload');
          if (rec.el.isConnected) body.innerHTML = html;
        })
        .catch(function () {
          if (rec.el.isConnected) showError(body, rec); /* never a dead end */
        });
    }

    function openFromIcon(icon) {
      var h = icon.getAttribute('data-log-handle') || icon.getAttribute('href') || '';
      if (!h) return;
      var existing = wins[h];
      if (existing) {
        if (existing.rolled) {
          existing.rolled = false;
          existing.el.removeAttribute('data-log-rolled');
        }
        if (existing.z !== zTop) { existing.z = ++zTop; }
        applyGeom(existing);
        focusWin(existing.el);
        save();
        return;
      }
      var rec = makeWin({
        h: h,
        u: icon.getAttribute('href') || '',
        t: fileName(icon.getAttribute('data-log-title') || h),
        z: ++zTop,
        rolled: false
      });
      if (!rec) return;
      loadContent(rec);
      focusWin(rec.el);
      save();
    }

    /* Adopt a server-rendered window (README.TXT / [data-log-preopened]) —
       merged into state and z-order WITHOUT re-fetching. */
    function adoptServer(w, savedRec) {
      var h = w.getAttribute('data-log-handle') || '';
      if (!h || wins[h]) return null;
      var titleEl = w.querySelector('[data-log-win-title]');
      var t = (savedRec && typeof savedRec.t === 'string' && savedRec.t) ||
        (titleEl ? titleEl.textContent.replace(/^\s+|\s+$/g, '') : h);
      var isPre = w.hasAttribute('data-log-preopened');
      var u = (savedRec && typeof savedRec.u === 'string' && savedRec.u) ||
        w.getAttribute('data-log-url') ||
        (isPre ? window.location.pathname : '');
      var rec = {
        h: h, u: u, t: t, el: w,
        rolled: !!(savedRec && savedRec.rolled),
        z: (savedRec && isNum(savedRec.z)) ? savedRec.z : ++zTop
      };
      if (savedRec && isNum(savedRec.x) && isNum(savedRec.y)) { rec.x = savedRec.x; rec.y = savedRec.y; }
      else { var p = cascade(); rec.x = p.x; rec.y = p.y; }
      dressWin(w, t);
      if (titleEl) titleEl.textContent = t;
      if (rec.rolled) w.setAttribute('data-log-rolled', '');
      else w.removeAttribute('data-log-rolled');
      applyGeom(rec);
      wins[h] = rec;
      return rec;
    }

    /* ---------- drag (pointer events; desktop + mouse/pen only) ---------- */

    function startDrag(w, tb, e) {
      if (mqMobile.matches || e.pointerType === 'touch' || e.button !== 0) return;
      var rec = recOf(w);
      if (!rec) return;
      e.preventDefault();
      var startX = e.clientX;
      var startY = e.clientY;
      var baseX = rec.x;
      var baseY = rec.y;
      var lw = layer.clientWidth;
      var lh = layer.clientHeight;
      var ww = w.offsetWidth;
      var wh = w.offsetHeight;
      w.setAttribute('data-log-dragging', '');
      try { tb.setPointerCapture(e.pointerId); } catch (err) {}
      function move(ev) {
        var x = clamp(baseX + (ev.clientX - startX), 0, Math.max(0, lw - ww));
        var y = clamp(baseY + (ev.clientY - startY), 0, Math.max(0, lh - wh));
        rec.x = x;
        rec.y = y;
        w.style.left = x + 'px';
        w.style.top = y + 'px';
      }
      function end() {
        tb.removeEventListener('pointermove', move);
        tb.removeEventListener('pointerup', end);
        tb.removeEventListener('pointercancel', end);
        w.removeAttribute('data-log-dragging');
        save();
      }
      tb.addEventListener('pointermove', move);
      tb.addEventListener('pointerup', end);
      tb.addEventListener('pointercancel', end);
    }

    /* ---------- delegated events ---------- */

    root.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      var t = e.target;
      if (!t || !t.closest) return;

      /* window chrome first (boxes may sit inside links never, but be safe) */
      var closeBtn = t.closest('[data-log-close]');
      if (closeBtn && root.contains(closeBtn)) {
        var wc = closeBtn.closest('[data-log-win]');
        if (wc) closeWin(wc, false);
        return;
      }
      var zoomBtn = t.closest('[data-log-zoom]');
      if (zoomBtn && root.contains(zoomBtn)) {
        var wz = zoomBtn.closest('[data-log-win]');
        if (wz) toggleRoll(wz);
        return;
      }

      /* icons: let modified clicks fall through to the browser (new tab etc.) */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;

      var up = t.closest('[data-log-up]');
      if (up && view.contains(up)) {
        /* on /tagged/ pages the server filtered the icon set — client-side
           navigation would show an incomplete root, so follow the real href */
        if (filteredPage) return;
        e.preventDefault();
        var fromR = layer.getBoundingClientRect();
        var toR = up.getBoundingClientRect();
        applyView('');
        flourish(fromR, toR);
        return;
      }

      var folder = t.closest('[data-log-folder]');
      if (folder && view.contains(folder)) {
        if (filteredPage) return;
        e.preventDefault();
        var h = folder.getAttribute('data-log-folder') || '';
        var iconR = folder.getBoundingClientRect();
        var layerR = layer.getBoundingClientRect();
        if (h && h === path) {
          /* re-clicking the open folder collapses back */
          applyView('');
          flourish(layerR, iconR);
        } else if (h) {
          applyView(h);
          flourish(iconR, layerR);
        }
        return;
      }

      var file = t.closest('[data-log-file]');
      if (file && view.contains(file)) {
        e.preventDefault();
        openFromIcon(file);
      }
    });

    /* z-order: any press inside a window fronts it; titlebar press starts drag */
    root.addEventListener('pointerdown', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var w = t.closest('[data-log-win]');
      if (!w || !layer.contains(w)) return;
      bringFront(w);
      var tb = t.closest('[data-log-titlebar]');
      if (tb && !t.closest('[data-log-close]') && !t.closest('[data-log-zoom]')) {
        startDrag(w, tb, e);
      }
    });

    /* double-click titlebar rolls up */
    root.addEventListener('dblclick', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-log-close]') || t.closest('[data-log-zoom]')) return;
      var tb = t.closest('[data-log-titlebar]');
      if (!tb) return;
      var w = tb.closest('[data-log-win]');
      if (w && layer.contains(w)) toggleRoll(w);
    });

    /* Escape closes the focused window, focus returns to its icon */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      var a = document.activeElement;
      if (!a || !root.contains(a) || !a.closest) return;
      var w = a.closest('[data-log-win]');
      if (w && layer.contains(w)) {
        e.preventDefault();
        closeWin(w, true);
      }
    });

    /* ---------- keep windows on the desk across resizes / mode flips ---------- */

    function clampAll() {
      if (mqMobile.matches) return;
      var lw = layer.clientWidth;
      var lh = layer.clientHeight;
      var moved = false;
      Object.keys(wins).forEach(function (k) {
        var rec = wins[k];
        var x = clamp(rec.x, 0, Math.max(0, lw - rec.el.offsetWidth));
        var y = clamp(rec.y, 0, Math.max(0, lh - rec.el.offsetHeight));
        if (x !== rec.x || y !== rec.y) {
          rec.x = x;
          rec.y = y;
          moved = true;
        }
        applyGeom(rec);
      });
      if (moved) save();
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(clampAll, 180);
    });
    if (typeof mqMobile.addEventListener === 'function') {
      mqMobile.addEventListener('change', clampAll);
    } else if (typeof mqMobile.addListener === 'function') {
      mqMobile.addListener(clampAll); /* older Safari */
    }

    window.addEventListener('pagehide', flushSave);

    /* ---------- boot: restore state, adopt server windows, set view ---------- */

    var saved = readStore();
    var validSaved = !!(saved && saved.v === 1);
    if (validSaved && isNum(saved.z) && saved.z > zTop) zTop = saved.z;

    /* server-rendered windows already in the layer (README.TXT, preopened article) */
    var pre = Object.create(null);
    var preList = layer.querySelectorAll('[data-log-win]');
    for (var pi = 0; pi < preList.length; pi++) {
      var ph = preList[pi].getAttribute('data-log-handle');
      if (ph) pre[ph] = preList[pi];
    }

    /* 1. restore saved windows (re-fetch content; keep positions/z/rolled) */
    if (validSaved && Array.isArray(saved.wins)) {
      saved.wins
        .slice()
        .sort(function (a, b) { return ((a && a.z) || 0) - ((b && b.z) || 0); })
        .forEach(function (r) {
          if (!r || typeof r.h !== 'string' || !r.h || wins[r.h]) return;
          if (pre[r.h]) {
            adoptServer(pre[r.h], r); /* server-rendered: adopt, do NOT re-fetch */
            delete pre[r.h];
            return;
          }
          if (r.h === '__readme') return; /* readme disabled this load: drop */
          if (typeof r.u !== 'string' || !r.u) return;
          var rec = makeWin({
            h: r.h,
            u: r.u,
            t: typeof r.t === 'string' && r.t ? r.t : fileName(r.h),
            x: isNum(r.x) ? r.x : null,
            y: isNum(r.y) ? r.y : null,
            z: isNum(r.z) ? r.z : ++zTop,
            rolled: !!r.rolled
          });
          if (rec) loadContent(rec);
        });
    }

    /* 2. leftover server windows not in saved state */
    Object.keys(pre).forEach(function (h) {
      var el = pre[h];
      if (!el || wins[h]) return;
      if (h === '__readme' && validSaved) {
        el.remove(); /* user closed README on a previous visit */
        return;
      }
      adoptServer(el, null); /* first-visit README, fresh preopened article, etc. */
    });

    /* keep zTop honest after adoption */
    Object.keys(wins).forEach(function (k) { if (wins[k].z > zTop) zTop = wins[k].z; });

    /* 3. the visited article's window always ends up front + unrolled */
    var preOpen = layer.querySelector('[data-log-win][data-log-preopened]');
    if (preOpen) {
      var por = recOf(preOpen);
      if (por) {
        por.rolled = false;
        preOpen.removeAttribute('data-log-rolled');
        por.z = ++zTop;
        applyGeom(por);
      }
    }

    /* 4. initial path: /tagged/ URL wins; else saved path; else root */
    var tagMatch = window.location.pathname.match(/\/tagged\/([^\/?#]+)/);
    var initPath = '';
    if (tagMatch) {
      try { initPath = decodeURIComponent(tagMatch[1]); } catch (err) { initPath = tagMatch[1]; }
    } else if (validSaved && typeof saved.path === 'string') {
      initPath = saved.path;
    }
    if (initPath && !hasFolder(initPath)) initPath = '';
    applyView(initPath, true);

    /* 5. persist the merged state (debounced; no-op if storage unavailable) */
    save();
  }

  /* ---------- global boot ---------- */

  function boot() {
    var roots = document.querySelectorAll('[data-log-desktop]');
    for (var i = 0; i < roots.length; i++) initDesktop(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* theme editor: re-init when the section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    var roots = e.target.querySelectorAll('[data-log-desktop]');
    for (var i = 0; i < roots.length; i++) initDesktop(roots[i]);
  });
})();
